// Fee-waiver basis record (SOR-237) — the durable record persisted on an order
// whose LDS fees the charge seam suppressed under a fee waiver, naming WHICH
// trigger fired and on which filing party.
//
// The record OBSERVES the suppression decision; it never re-decides it. The
// charge seam (`suppressLdsFee` in api-gateway's submitOrder) stays the one
// decider of WHETHER LDS fees are $0 — this module only answers what nothing
// else answers: which trigger and which party to name. It reuses the shared
// predicates in ./fee-waiver.js (`partyRequestsFeeWaiver`, `feeWaiverSatisfied`,
// `isFeeWaiverRequestDoc`) so the record's trigger can never disagree with the
// suppression that produced it — a second derivation of the same fact is
// exactly the drift this record exists to close.
//
// Purity: no clock, no I/O, no DB — same discipline as fee-waiver.ts.
// Carriage: pointers only (SPEC-SOR-234 R8). A record references its evidence
// (the FW-001 order document, the court-returned waiver-status fields, the NFRC
// acceptance) by scalar pointer and never embeds document bytes, base64,
// content, a signed download URL, or a copied NFRC payload. This module owns
// BOTH the record's shape and its population policy: `resolveFeeWaiverBasis`
// names the trigger (SOR-237), `attachFeeWaiverEvidence` attaches the evidence
// pointers to the record that decision already produced (SOR-240) — it never
// re-decides the trigger.
import { z } from 'zod/v4';
import { JTI_FEE_WAIVER_DOC_CODE, feeWaiverSatisfied, isFeeWaiverRequestDoc, partyRequestsFeeWaiver, } from './fee-waiver.js';
// ---------------------------------------------------------------------------
// Record schema
// ---------------------------------------------------------------------------
/** The closed set of fee-waiver triggers a basis record can name. */
export const FeeWaiverTriggerSchema = z.enum([
    /** A satisfied filing party's connected FW-001 drove the suppression. */
    'party_fw001_attached',
    /** A satisfied party's on-file assertion drove it (no FW-001 connected). */
    'party_on_file_asserted',
    /** Only the account-level fee-waiver (Pro Per) category did. */
    'account_pro_per',
]);
/**
 * The fee-waiver basis record. Evidence is carried by POINTER only — the
 * scalar reference fields below never hold document content (R8).
 */
export const FeeWaiverBasisRecordSchema = z.object({
    /** Which fee-waiver trigger fired (closed enum). */
    trigger: FeeWaiverTriggerSchema,
    /**
     * Roster-party id of the filing party whose satisfied waiver fired. Named on
     * every party-level record; absent exactly on `account_pro_per` (R4/R5).
     */
    partyId: z.string().optional(),
    /** Pointer: the connected FW-001 order document's id (never its bytes). */
    fw001DocumentId: z.string().optional(),
    /**
     * Pointer: the court-returned waiver-status fields for the record's party
     * (CaseParticipantExt efspFeeWaiverFiled / efmExemptionType). The value space
     * has only `reported` — presence means the court supplied them, absence means
     * it did not; there is no `false` member ("absent yields undefined, never
     * false").
     */
    courtReturnedFieldsRef: z.literal('reported').optional(),
    /** Pointer: the NFRC acceptance / filing reference (never the payload). */
    acceptanceRef: z.string().optional(),
});
// ---------------------------------------------------------------------------
// Resolver
// ---------------------------------------------------------------------------
/**
 * The connected FW-001 document, if the filing carries one. The exact universal
 * code (`JTI_FEE_WAIVER_DOC_CODE`) is matched first, then the shared
 * name-based predicate — the same precedence `isFeeWaiverRequestDoc` applies,
 * so the pointer always names a document the JTI fee engine actually credits.
 */
function findConnectedFw001(documents) {
    return (documents.find((d) => d.documentType?.code === JTI_FEE_WAIVER_DOC_CODE) ??
        documents.find((d) => isFeeWaiverRequestDoc(d.documentType)));
}
/**
 * The court-returned evidence pointer for a party-level record: `'reported'`
 * when the party carries ANY court-returned waiver field, nothing otherwise.
 * Presence is the whole message — the court supplied the fields — so an
 * all-empty `courtWaiver` object (the court said nothing) points nowhere, and
 * the pointer is decoupled from `efspFeeWaiverFiled`'s tri-state (a court that
 * returned only an `efmExemptionType` still reported). Only meaningful on
 * party-level records: `account_pro_per` names no party, so there is no
 * court-returned participant data for it to point at.
 */
function courtReturnedFieldsPointer(party) {
    const cw = party.courtWaiver;
    const reported = cw !== undefined &&
        (cw.efspFeeWaiverFiled !== undefined ||
            cw.efmExemptionType !== undefined ||
            cw.efmFeeExemptionType !== undefined);
    return reported ? { courtReturnedFieldsRef: 'reported' } : {};
}
/**
 * Resolve which fee-waiver trigger explains this filing's suppression, and on
 * whose behalf. Pure and total — returns `null` when nothing qualifies.
 *
 * Trigger precedence is most-specific-wins (the spec's recorded assumption):
 * FW-001 over on-file over account.
 *  1. a party whose effective exemption is `FEE_WAIVER` with a connected FW-001
 *     → `party_fw001_attached`, naming that party + the FW-001 document id;
 *  2. a satisfied party with `feeWaiverOnFile === true` and NO connected FW-001
 *     → `party_on_file_asserted`, naming that party;
 *  3. no qualifying party but the account is a fee-waiver (Pro Per) account
 *     → `account_pro_per`, naming NO party;
 *  4. otherwise `null`.
 *
 * Every party-level record names a party whose OWN effective `FEE_WAIVER` is
 * satisfied (R4), by construction of the two branches. Parties are scanned in
 * roster order, so the record is deterministic when several qualify. Whether a
 * record is written AT ALL is the caller's charge-seam decision
 * (`suppressLdsFee`) — this function never sees `source` and can never cause or
 * prevent a $0.
 */
export function resolveFeeWaiverBasis(parties, documents, isFeeWaiverAccount) {
    const fw001 = findConnectedFw001(documents);
    if (fw001) {
        // A connected FW-001 satisfies EVERY fee-waiver-requesting party at once,
        // so the first requesting party in roster order is the firing party.
        const party = parties.find((p) => partyRequestsFeeWaiver(p) && feeWaiverSatisfied(p, documents));
        if (party) {
            return {
                trigger: 'party_fw001_attached',
                ...(party.id ? { partyId: party.id } : {}),
                ...(fw001.id ? { fw001DocumentId: fw001.id } : {}),
                ...courtReturnedFieldsPointer(party),
            };
        }
    }
    if (!fw001) {
        // No FW-001 anywhere: an on-file assertion is the only party-level route.
        const party = parties.find((p) => partyRequestsFeeWaiver(p) &&
            p.feeWaiverOnFile === true &&
            feeWaiverSatisfied(p, documents));
        if (party) {
            return {
                trigger: 'party_on_file_asserted',
                ...(party.id ? { partyId: party.id } : {}),
                ...courtReturnedFieldsPointer(party),
            };
        }
    }
    if (isFeeWaiverAccount) {
        return { trigger: 'account_pro_per' };
    }
    return null;
}
/**
 * Attach the evidence pointers to a record `resolveFeeWaiverBasis` already
 * produced. Pure and total; returns a NEW record — the input is never mutated.
 *
 * At most three keys are added, each only when its evidence exists, each left
 * ABSENT (never '' / null) when it does not (R7):
 *  - `fw001DocumentId` — the connected FW-001's ORDER-DOCUMENT row id, found by
 *    the module's own `findConnectedFw001` precedence (universal
 *    `JTI_FEE_WAIVER_DOC_CODE` first, then the shared `isFeeWaiverRequestDoc`
 *    name predicate) — the SAME predicate that decided the trigger, so pointer
 *    and trigger can never disagree. Replaces the wizard-minted document id the
 *    resolver carried; left untouched when the rows resolve no FW-001, so a
 *    pointer is never cleared for lack of a replacement.
 *  - `courtReturnedFieldsRef: 'reported'` — when the record's NAMED party
 *    carries the court-returned waiver group (group presence is the trigger;
 *    `account_pro_per` names no party and can never gain this pointer).
 *  - `acceptanceRef` — when a non-empty filing reference was supplied.
 *
 * `trigger` and `partyId` pass through untouched: the enrichment attaches
 * evidence to the charge-seam decision, it never re-decides it, and a missing
 * pointer never changes the trigger.
 */
export function attachFeeWaiverEvidence(record, evidence) {
    const fw001 = findConnectedFw001(evidence.documents);
    const namedParty = record.partyId !== undefined
        ? evidence.parties.find((p) => p.id === record.partyId)
        : undefined;
    return {
        ...record,
        ...(fw001?.id ? { fw001DocumentId: fw001.id } : {}),
        ...(namedParty?.courtWaiver !== undefined ? { courtReturnedFieldsRef: 'reported' } : {}),
        ...(evidence.acceptanceRef ? { acceptanceRef: evidence.acceptanceRef } : {}),
    };
}
// Acceptance-pointer seam helper
// ---------------------------------------------------------------------------
/**
 * Stamp the NFRC acceptance pointer onto a resolved record, at the charge seam
 * — deliberately NOT part of `resolveFeeWaiverBasis`: the filing reference is
 * minted by the vendor call AFTER the record is resolved, and a fourth
 * parameter would break the 3-arg signature every proptest shard and test call
 * site pins. Returns the record unchanged (same value) when there is nothing
 * to point at — `efmFilingId` is assigned only on CONFIRMED submits, so the
 * pointer never carries a reference the court did not confirm; the empty-string
 * guard is belt-and-suspenders against a Success carrying a blank id (the
 * PR #726 no-false-positive discipline).
 */
export function stampAcceptanceRef(record, acceptanceRef) {
    if (!acceptanceRef)
        return record;
    return { ...record, acceptanceRef };
}
